import { useState, useEffect } from "react";
import { Icons } from "../../components/common/Icons";
import api from "../../services/api";
import { useEmployeeData } from "../../context/EmployeeContext";
import EmpModal from "./EmpModal";
import "../../assets/styles/Employee/EmpHelpDeskPage.css";

const priColor = (p) => ({ Critical:"#dc2626", High:"#ef4444", Medium:"#d97706", Low:"#2563eb" }[p] || "#94a3b8");

const fmtDate = (t) => {
  if (t?.date && String(t.date).trim()) return t.date;
  if (t?.created_at) {
    const d = new Date(t.created_at);
    if (!isNaN(d)) return d.toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
  }
  return "—";
};

// Employee only sees the final reply from the handler — never internal PM notes
const getReply = (t) => t?.final_reply || t?.hr_reply || "";

// ── Escalation Modal ──
function EscalateModal({ ticket, onClose, onDone }) {
  const [pms, setPms] = useState([]);
  const [pmId, setPmId] = useState("");
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getEscalablePMs()
      .then(d => { setPms(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const send = async () => {
    if (!pmId) { alert("Please select a PM"); return; }
    setSending(true);
    try {
      await api.escalateTicket({
        ticketId: ticket.ticket_id || ticket.id,
        pmId,
        reason: reason.trim() || "Employee escalated this ticket to PM for further action"
      });
      onDone(pms.find(p => String(p.id) === String(pmId))?.name || "PM");
    } catch (e) {
      alert("Error: " + e.message);
    }
    setSending(false);
  };

  const handler = (() => {
    const ot = String(ticket?.original_target || ticket?.category || "").toLowerCase();
    if (ot === "hr") return "HR";
    if (ot === "itadmin" || ot === "it admin") return "IT Admin";
    return "the handler";
  })();

  return (
    <EmpModal title="🚨 Escalate to Project Manager" onClose={onClose}>
      <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8,
        padding:"10px 14px", marginBottom:16 }}>
        <div style={{ fontSize:11, color:"#dc2626", fontWeight:700, marginBottom:4 }}>TICKET</div>
        <div style={{ fontWeight:600, fontSize:13, color:"#1e293b" }}>{ticket.subject}</div>
        <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>
          Priority: <b style={{ color:priColor(ticket.priority) }}>{ticket.priority||"Medium"}</b>
          &nbsp;·&nbsp;Handler: <b>{handler}</b>
          &nbsp;·&nbsp;Status: {ticket.status}
        </div>
      </div>

      <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8,
        padding:"10px 14px", marginBottom:16, fontSize:12, color:"#92400e", lineHeight:1.6 }}>
        <b>What happens next:</b><br/>
        PM will review the situation and send professional instructions to <b>{handler}</b> to resolve your ticket.
        You will receive a notification once <b>{handler}</b> has responded.
        The PM's communication with <b>{handler}</b> remains internal.
      </div>

      <div className="form-group">
        <label className="form-label">Select Project Manager *</label>
        {loading ? (
          <div style={{ padding:"10px", color:"#94a3b8", fontSize:13 }}>Loading PMs…</div>
        ) : pms.length === 0 ? (
          <div style={{ padding:"10px", color:"#ef4444", fontSize:13 }}>No PMs available</div>
        ) : (
          <select className="form-input" value={pmId} onChange={e => setPmId(e.target.value)}>
            <option value="">— Select PM —</option>
            {pms.map(p => (
              <option key={p.id} value={p.id}>{p.name} {p.emp_id ? `(${p.emp_id})` : ""}</option>
            ))}
          </select>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">Reason for Escalation</label>
        <textarea className="form-input" rows={3}
          placeholder="Why are you escalating? (optional)"
          value={reason} onChange={e => setReason(e.target.value)} />
      </div>

      <div className="form-actions">
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button
          onClick={send}
          disabled={sending || !pmId}
          style={{ background:"#dc2626", color:"#fff", border:"none", borderRadius:8,
            padding:"9px 20px", fontWeight:700, cursor:"pointer", opacity:(!pmId||sending)?0.6:1 }}>
          {sending ? "Escalating…" : "🚨 Escalate to PM"}
        </button>
      </div>
    </EmpModal>
  );
}

// ── Main ──
export default function EmpHelpDeskPage() {
  const { empTickets } = useEmployeeData();
  const [tickets, setTickets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [escalateTicket, setEscalateTicket] = useState(null);
  const [suc, setSuc] = useState("");

  useEffect(() => { if (empTickets?.length) setTickets(empTickets); }, [empTickets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const sub = fd.get("title"), cat = fd.get("toRole"),
          pri = fd.get("priority"), desc = fd.get("comments"),
          ticketType = fd.get("ticketType");
    if (!sub || !cat || !desc) return;
    try {
      const category = cat === "pm" ? "PM" : cat === "hr" ? "HR" : "IT Admin";
      const result = await api.createTicket({ subject:sub, category, priority:pri||"Medium", description:desc, ticketType:ticketType||"General" });
      const newTkt = {
        id: result.ticketId || "TKT-" + String(tickets.length+1).padStart(3,"0"),
        ticket_id: result.ticketId,
        subject:sub, category, priority:pri||"Medium", description:desc,
        status:"open", date: new Date().toLocaleDateString("en-US",{month:"short",day:"2-digit"}),
        created_at: new Date().toISOString(),
        original_target: cat
      };
      setTickets(prev => [newTkt, ...prev]);
      setShowForm(false);
      setSuc(`✅ Ticket submitted to ${category}. You will be notified when they respond.`);
      setTimeout(() => setSuc(""), 6000);
    } catch (err) { setSuc("Error: " + err.message); setTimeout(() => setSuc(""), 5000); }
  };

  const handleEscalateDone = (pmName) => {
    setEscalateTicket(null);
    setSuc(`✅ Escalated to PM ${pmName}. You will be notified once the handler responds.`);
    setTimeout(() => setSuc(""), 6000);
    api.getTickets?.().then(d => { if (Array.isArray(d)) setTickets(d); }).catch(()=>{});
  };

  const canEscalate = (t) => {
    const s = String(t.status || "").toLowerCase();
    // Can escalate if ticket is not yet resolved/closed, and is for IT Admin or HR (not already PM-direct)
    const ot = String(t.original_target || t.category || "").toLowerCase();
    const isHandlerTicket = ot === "itadmin" || ot === "it admin" || ot === "hr";
    return isHandlerTicket && !["resolved","closed"].includes(s) && !t.escalated_to_pm;
  };

  const targetLabel = (t) => {
    const ot = String(t.original_target || t.category || t.assigned_role || "").toLowerCase();
    if (ot === "itadmin" || ot === "it admin") return <span style={{ color:"#4f46e5", fontWeight:600, fontSize:11 }}>IT Admin</span>;
    if (ot === "hr") return <span style={{ color:"#059669", fontWeight:600, fontSize:11 }}>HR</span>;
    return <span style={{ color:"#7c3aed", fontWeight:600, fontSize:11 }}>PM</span>;
  };

  const statusLabel = (t) => {
    const s = String(t.status || "open");
    if (t.escalated_to_pm && !["resolved","closed"].includes(s))
      return <span className="status-badge in_progress"><span className="sb-dot"/>escalated</span>;
    if (t.handler_escalated_to_pm && !t.pm_response_to_handler && !["resolved","closed"].includes(s))
      return <span className="status-badge in_progress"><span className="sb-dot"/>under review</span>;
    return (
      <span className={`status-badge ${s}`}>
        <span className="sb-dot" />
        {s.replace("_"," ")}
      </span>
    );
  };

  return (
    <div>
      {suc && (
        <div style={{ background:suc.startsWith("Error")?"#fef2f2":"#eff6ff",
          color:suc.startsWith("Error")?"#dc2626":"#2563eb",
          border:`1px solid ${suc.startsWith("Error")?"#fecaca":"#bfdbfe"}`,
          borderRadius:8, padding:"10px 14px", marginBottom:12, fontSize:13, fontWeight:600 }}>
          {suc}
        </div>
      )}

      <div className="card animate-in">
        <div className="card-header">
          <div className="card-title">My Help Desk Tickets</div>
          <button className="btn-outline emphelpdeskpage-inline-2" onClick={() => setShowForm(true)}>
            <Icons.Plus /> Raise Ticket
          </button>
        </div>
        <div className="card-body emphelpdeskpage-inline-3">
          <table className="data-table">
            <thead>
              <tr>
                <th>Subject</th><th>To</th><th>Priority</th>
                <th>Date</th><th>Status</th><th>Response</th><th>Action</th>
              </tr>
            </thead>
            <tbody>
              {tickets.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign:"center", padding:20, color:"#94a3b8" }}>No tickets yet</td></tr>
              ) : tickets.map(t => {
                const reply = getReply(t);
                return (
                  <tr key={t.id||t.ticket_id}>
                    <td className="emphelpdeskpage-inline-5">{t.subject}</td>
                    <td>{targetLabel(t)}</td>
                    <td>
                      <span style={{ background:priColor(t.priority)+"18", color:priColor(t.priority),
                        padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:700 }}>
                        {t.priority||"Medium"}
                      </span>
                    </td>
                    <td className="emphelpdeskpage-inline-6">{fmtDate(t)}</td>
                    <td>{statusLabel(t)}</td>
                    <td className="emphelpdeskpage-inline-7">
                      {reply ? (
                        <span className="emphelpdeskpage-inline-8" title={reply}>{reply.slice(0,50)}{reply.length>50?"…":""}</span>
                      ) : (
                        <span style={{ fontSize:11, color:"#94a3b8", fontStyle:"italic" }}>Awaiting response…</span>
                      )}
                    </td>
                    <td>
                      {canEscalate(t) && (
                        <button
                          onClick={() => setEscalateTicket(t)}
                          style={{ background:"#fef2f2", color:"#dc2626", border:"1px solid #fecaca",
                            borderRadius:6, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer",
                            whiteSpace:"nowrap" }}>
                          🚨 Escalate to PM
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Raise ticket modal */}
      {showForm && (
        <EmpModal title="Raise Help Desk Ticket" onClose={() => setShowForm(false)}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Subject *</label>
              <input name="title" className="form-input" placeholder="Brief description of your issue" required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Send To *</label>
                <select name="toRole" className="form-input" required>
                  <option value="">— Select —</option>
                  <option value="hr">HR</option>
                  <option value="itadmin">IT Admin</option>
                  <option value="pm">Project Manager</option>
                </select>
                <div style={{ fontSize:10, color:"#64748b", marginTop:3 }}>
                  Ticket goes directly to the selected team.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select name="priority" className="form-input">
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                  <option value="Low">Low</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Type</label>
              <select name="ticketType" className="form-input">
                <option value="General">General</option>
                <option value="Access">Access</option>
                <option value="Payroll">Payroll</option>
                <option value="Asset">Asset</option>
                <option value="Leave">Leave</option>
                <option value="Resignation">Resignation</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea name="comments" className="form-input" rows="4" placeholder="Describe your issue in detail…" required />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn-submit">Submit Ticket</button>
            </div>
          </form>
        </EmpModal>
      )}

      {/* Escalate modal */}
      {escalateTicket && (
        <EscalateModal
          ticket={escalateTicket}
          onClose={() => setEscalateTicket(null)}
          onDone={handleEscalateDone}
        />
      )}
    </div>
  );
}
